import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTermsOfService } from '../../hooks/useTermsOfService';
import { publicRoutes } from '../../config/routes';
import { cn } from '../../lib/utils';
import { AlertBanner, LoadingSpinner } from '../../components/Common';

// Public route paths where the ToS gate should not block (e.g. homepage, login)
const publicRoutePaths = publicRoutes.map((route) => route.path);

const TosGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { activeTos, userTosStatus, loading, acceptTos, refreshUserStatus } = useTermsOfService(user?.id);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullText, setShowFullText] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine if user needs to accept TOS
  // Require positive confirmation: userTosStatus must be loaded (non-null)
  // with has_accepted_current_tos explicitly false. This prevents the modal
  // from flashing on refresh while the status is still being fetched.
  const needsAcceptance = user && activeTos && userTosStatus && userTosStatus.has_accepted_current_tos === false;

  // Don't block public routes - allows browser back button to work correctly
  const isPublicRoute = publicRoutePaths.includes(location.pathname);

  // Don't show gate while loading, if no user, or if TOS status hasn't loaded yet
  if (!user || loading || (user && activeTos && !userTosStatus)) {
    return <>{children}</>;
  }

  // User has accepted current TOS, no active TOS exists, or on a public route - allow access
  if (!needsAcceptance || isPublicRoute) {
    return <>{children}</>;
  }

  const handleDecline = async () => {
    setDeclining(true);
    setError(null);

    try {
      await signOut();
      navigate('/');
    } catch (err: any) {
      console.error('Error signing out:', err);
      setError('Failed to sign out. Please try again.');
    } finally {
      setDeclining(false);
    }
  };

  const handleAccept = async () => {
    if (!activeTos) return;

    setAccepting(true);
    setError(null);

    try {
      // Get IP and user agent for audit trail
      const userAgent = navigator.userAgent;

      const { success, error: acceptError } = await acceptTos(
        activeTos.tos_id,
        undefined, // IP address (backend can capture this if needed)
        userAgent
      );

      if (!success) {
        throw new Error(acceptError || 'Failed to accept Terms of Service');
      }

      // Refresh user status to reflect acceptance
      await refreshUserStatus();
    } catch (err: any) {
      console.error('Error accepting TOS:', err);
      setError(err.message || 'Failed to accept Terms of Service. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      {/* Blocking Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto bg-navy-900/70">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-4xl card shadow-xl">
            {/* Header */}
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center gap-3 mb-2">
                <svg
                  className="w-7 h-7 text-navy-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.25}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h2 className="font-serif font-normal text-navy-900" style={{ fontSize: 22 }}>
                  Terms of Service required
                </h2>
              </div>
              <p className="text-sm text-ink-500">
                Please review and accept our Terms of Service to continue using Integrity Research.
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* TOS Info */}
              <div className="mb-6 p-4 bg-paper-2 border border-stone-200 rounded">
                <h3 className="font-serif font-normal text-navy-900 mb-1" style={{ fontSize: 18 }}>
                  {activeTos.title}
                </h3>
                <p className="text-sm text-ink-500">
                  Version {activeTos.version} • Effective{' '}
                  {new Date(activeTos.effective_date).toLocaleDateString()}
                </p>
              </div>

              {/* TOS Content Preview/Full */}
              <div className="mb-6">
                <div
                  className={cn(
                    'p-6 bg-paper-2 border border-stone-200 rounded overflow-y-auto transition-all',
                    showFullText ? 'max-h-[50vh]' : 'max-h-64'
                  )}
                >
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown>{activeTos.content}</ReactMarkdown>
                  </div>
                </div>

                {/* Toggle full text button */}
                <button
                  onClick={() => setShowFullText(!showFullText)}
                  className="mt-2 text-sm text-navy-700 hover:underline"
                >
                  {showFullText ? 'Show less' : 'Show full Terms of Service'}
                </button>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-6">
                  <AlertBanner variant="error">{error}</AlertBanner>
                </div>
              )}

              {/* Notice */}
              <div className="p-4 bg-caution-100 border border-stone-300 rounded">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-caution-700 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.25}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm text-caution-700">
                    You must accept the Terms of Service to access Integrity Research.
                    By selecting &ldquo;I accept&rdquo;, you agree to be bound by these terms.
                    If you decline, you will be signed out.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-stone-200 bg-paper-2">
              <button
                onClick={handleDecline}
                disabled={declining || accepting}
                className={cn(
                  'btn-outline',
                  (declining || accepting) && 'opacity-50 cursor-not-allowed'
                )}
              >
                {declining ? 'Signing out…' : 'Decline and sign out'}
              </button>
              <button
                onClick={handleAccept}
                disabled={accepting || declining}
                className={cn(
                  'btn-primary px-8',
                  (accepting || declining) && 'opacity-50 cursor-not-allowed'
                )}
              >
                {accepting ? (
                  <span className="flex items-center gap-2">
                    <LoadingSpinner />
                    Accepting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.25}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    I accept
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TosGate;
